import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Expand } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Img, RedactOverlay, useRedacted } from "@/components/common";
import { Screen } from "./Frames";

export const Gallery = ({ images, alt }) => {
  const [open, setOpen] = useState(null);
  const redacted = useRedacted();
  const move = (d) => setOpen((i) => (i + d + images.length) % images.length);
  return (
    <>
      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3" data-testid="frame-gallery">
        {images.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => setOpen(i)}
            data-testid={`gallery-item-${i}`}
            aria-label={`Open image ${i + 1} of ${images.length}`}
            className="group relative mb-4 block w-full overflow-hidden rounded-md bg-surface"
          >
            <Img src={src} alt={`${alt} ${i + 1}`} sizes="(max-width: 640px) 100vw, 33vw" className="h-auto w-full transition-transform duration-700 group-hover:scale-[1.03]" />
            {redacted && <RedactOverlay compact />}
            <span className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden>
              <Expand className="h-4 w-4" />
            </span>
          </button>
        ))}
      </div>
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-[min(92vw,1200px)] border-line/20 bg-canvas/95 p-3 backdrop-blur-xl sm:p-4"
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") move(1);
            if (e.key === "ArrowLeft") move(-1);
          }}
        >
          <DialogTitle className="sr-only">{alt} — image {open + 1}</DialogTitle>
          {open !== null && (
            <div className="relative">
              <Img src={images[open]} alt={`${alt} ${open + 1}`} eager sizes="92vw" className="mx-auto max-h-[78vh] w-auto rounded" />
              {redacted && <RedactOverlay />}
              <div className="mt-3 flex items-center justify-between">
                <span className="label-mono">{String(open + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => move(-1)} data-testid="lightbox-prev" aria-label="Previous image" className="grid h-10 w-10 place-items-center rounded-full border border-line/20 hover:border-turq hover:text-turq"><ArrowLeft className="h-4 w-4" /></button>
                  <button type="button" onClick={() => move(1)} data-testid="lightbox-next" aria-label="Next image" className="grid h-10 w-10 place-items-center rounded-full border border-line/20 hover:border-turq hover:text-turq"><ArrowRight className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MediaCarousel = ({ images, alt }) => {
  const [ref, embla] = useEmblaCarousel({ align: "start", loop: false });
  const [index, setIndex] = useState(0);
  const onSelect = useCallback(() => embla && setIndex(embla.selectedScrollSnap()), [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    return () => embla.off("select", onSelect);
  }, [embla, onSelect]);
  return (
    <div data-testid="frame-carousel" role="region" aria-roledescription="carousel" aria-label={alt}>
      <div ref={ref} className="overflow-hidden">
        <div className="flex gap-5">
          {images.map((src, i) => (
            <div key={src + i} className="min-w-0 shrink-0 basis-[88%] md:basis-[72%]" role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${images.length}`}>
              <Screen src={src} alt={`${alt} ${i + 1}`} ratio="3/2" className="rounded-md ring-1 ring-line/10" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {images.map((_, i) => (
            <button key={i} type="button" onClick={() => embla?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`} data-testid={`carousel-dot-${i}`} className={`h-[3px] rounded-full transition-[width,background-color] duration-500 ${i === index ? "w-10 bg-turq" : "w-5 bg-line/25 hover:bg-line/50"}`} />
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => embla?.scrollPrev()} data-testid="carousel-prev" aria-label="Previous slide" className="grid h-11 w-11 place-items-center rounded-full border border-line/20 transition-colors hover:border-turq hover:text-turq"><ArrowLeft className="h-4 w-4" /></button>
          <button type="button" onClick={() => embla?.scrollNext()} data-testid="carousel-next" aria-label="Next slide" className="grid h-11 w-11 place-items-center rounded-full border border-line/20 transition-colors hover:border-turq hover:text-turq"><ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};
